import { DayPicker, type DayPickerProps } from 'react-day-picker'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export type CalendarProps = DayPickerProps & {
	/** Extra class on the outermost wrapper. */
	className?: string
}

/**
 * Shadcn-style Calendar — react-day-picker DayPicker with our design tokens.
 * Use directly for inline calendars or via `DatePicker` for a popover trigger.
 */
function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn('p-3', className)}
			classNames={{
				months:    'flex flex-col sm:flex-row gap-4',
				month:     'flex flex-col gap-3',
				month_caption: 'flex justify-center items-center h-9',
				caption_label: 'text-sm font-[500] text-(--color-foreground)',
				nav:       'flex items-center justify-between absolute inset-x-3 top-3',
				button_previous: cn(
					'inline-flex items-center justify-center size-7 rounded-(--radius-sm)',
					'text-(--color-muted-foreground) hover:bg-(--color-muted) hover:text-(--color-foreground)',
				),
				button_next: cn(
					'inline-flex items-center justify-center size-7 rounded-(--radius-sm)',
					'text-(--color-muted-foreground) hover:bg-(--color-muted) hover:text-(--color-foreground)',
				),
				month_grid: 'w-full border-collapse',
				weekdays:   'flex',
				weekday:    'w-9 text-center text-xs font-[400] text-(--color-muted-foreground)',
				week:       'flex w-full mt-1',
				day:        'size-9 p-0 text-center text-sm relative focus-within:relative focus-within:z-20',
				day_button: cn(
					'inline-flex size-9 items-center justify-center rounded-(--radius-sm)',
					'text-(--color-foreground) hover:bg-(--color-muted) focus:outline-none focus-visible:ring-2 focus-visible:ring-(--color-primary)/40',
					'aria-selected:bg-(--color-primary) aria-selected:text-(--color-primary-foreground) aria-selected:hover:bg-(--color-primary)',
				),
				today:    'font-[600]',
				outside:  'text-(--color-muted-foreground) opacity-50',
				disabled: 'text-(--color-muted-foreground) opacity-40 cursor-not-allowed',
				hidden:   'invisible',
				range_start: 'rounded-l-(--radius-sm) bg-(--color-primary)/10',
				range_middle: 'rounded-none bg-(--color-primary)/10 [&_button]:bg-transparent [&_button]:text-(--color-foreground)',
				range_end:   'rounded-r-(--radius-sm) bg-(--color-primary)/10',
				...classNames,
			}}
			components={{
				Chevron: ({ orientation, ...rest }) =>
					orientation === 'left'
						? <ChevronLeftIcon className="size-4" {...rest} />
						: <ChevronRightIcon className="size-4" {...rest} />,
			}}
			{...props}
		/>
	)
}

export { Calendar }
